import React, { useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Divider, Grid, Hidden, TextField, Typography } from '@mui/material';
import ContainerBox from 'components/ContainerBox';
import ImageUpload from 'components/ImageUpload';
import { updateLocaleField, getLocaleFieldOrInitialValue } from 'common/utils/languageUtils';
import moment from 'moment-timezone';

import * as ActiveProductActions from 'actions/ActiveProductActions';
import * as ActiveProductSelectors from 'selectors/ActiveProductSelectors';
import * as ShopSelectors from 'selectors/ShopSelectors';
import * as UserSelectors from 'selectors/UserSelectors';
import useDebouncedForm from 'common/hooks/useDebouncedForm';

import CategorySelect from 'views/Product/__components/CategorySelect';
import VatSelect from 'components/VatSelect';
import { useTranslation } from 'services/localization/useTranslation';
import { isLiftTicketProduct } from 'common/utils/liftTicketUtils';
import { getProductImageSrcs, toMultiImgFormat } from 'common/utils/productUtils';

import SetProducts from 'views/Product/EditProduct/General/Sections/SetProducts';
import PackageTicketProducts from './Sections/PackageTicketProducts';
import useShopFormat from 'hooks/useShopFormat';
import PublicSelect from 'views/Product/__components/PublicSelect';
import HasFeature from 'components/features/HasFeature';
import CheckboxItem from 'common/components/CheckboxItem';
import AddOnPromoBlock from 'components/features/promos/AddOnPromoBlock';
import ExternalLink from 'components/ExternalLink';
import { Trans } from 'services/localization';
import { PACKAGE_PRODUCTS } from 'common/modules/support/articles';

const About = () => {
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const shopId = useSelector(ShopSelectors.activeShopId)!;
	const shopDefaultLanguage = useSelector(ShopSelectors.activeShopDefaultLanguage);
	const product = useSelector(ActiveProductSelectors.productDataWithChanges);
	const isEditingDisabled = useSelector(ActiveProductSelectors.isEditingDisabled);
	const isAdmin = useSelector(UserSelectors.isAdmin);
	const productImageUrls = getProductImageSrcs(product);
	const { localFormat } = useShopFormat();

	const isSetProduct = useSelector(ActiveProductSelectors.isSetProduct);
	const isSkidataProduct = useSelector(ActiveProductSelectors.isSkidataProduct);
	const isSkidataPackageProduct = useSelector(ActiveProductSelectors.isSkidataPackageProduct);
	const skidataValidity = product?.skidataProperties?.validity;

	const { state, editState } = useDebouncedForm({
		memoizedInitialState: useMemo(
			() => ({
				name: getLocaleFieldOrInitialValue(product?.name),
				description: getLocaleFieldOrInitialValue(product?.description),
				categoryIds: product?.categoryIds ?? [],
				vatPercent: product?.vatPercent,
				external: product?.external ?? false,
				images: productImageUrls,
				image: product?.image,
				additionalProduct: product?.additionalProduct ?? false,
				set: product?.set,
				setProductIds: product?.setProductIds,
			}),
			// eslint-disable-next-line react-hooks/exhaustive-deps
			[product],
		),
		memoizedOnDebounce: useCallback(
			(changes) => {
				dispatch(ActiveProductActions.editLocalProduct(changes));
			},
			[dispatch],
		),
	});

	return (
		<Grid container spacing={2}>
			<Grid item xs={12} md={8}>
				<Grid container spacing={2}>
					<Hidden mdUp>
						<Grid item xs={12} md={12}>
							<ContainerBox margin="none">
								<PublicSelect
									value={!state.additionalProduct}
									onChange={(value) => editState({ additionalProduct: !value })}
									disabled={isEditingDisabled}
								/>
							</ContainerBox>
						</Grid>
					</Hidden>
					<Grid item xs={12} md={12}>
						<ContainerBox margin="none">
							<Grid container spacing={2}>
								<Grid item xs={12}>
									<TextField
										variant="standard"
										fullWidth
										label={t('common:form.name', 'Name')}
										name={'name'}
										value={state.name.def}
										onChange={(e) =>
											editState({
												name: updateLocaleField(state.name, e.target.value, shopDefaultLanguage),
											})
										}
										margin="normal"
										type="text"
										disabled={isEditingDisabled}
									/>
								</Grid>
								{isLiftTicketProduct(product) && !!product.externalName && (
									<Grid item xs={12}>
										<TextField
											variant="standard"
											fullWidth
											label={t('product.DTAName', 'Skidata DTA name')}
											name={'external'}
											value={product.externalName.def}
											margin="normal"
											type="text"
											disabled={true}
										/>
									</Grid>
								)}
								{!!skidataValidity && (
									<Grid item xs={12}>
										<TextField
											variant="standard"
											fullWidth
											label={t(
												'skidata.validityDateRange',
												'Skidata DTA validity period for the ticket',
											)}
											name={'validity'}
											value={`${localFormat(
												moment(skidataValidity.from).toISOString(),
												'DD.MM.YYYY',
											)} - ${localFormat(moment(skidataValidity.to).toISOString(), 'DD.MM.YYYY')}`}
											margin="normal"
											type="text"
											disabled={true}
										/>
									</Grid>
								)}
								{!state.external && (
									<Grid item xs={12}>
										<VatSelect
											value={state.vatPercent}
											onChange={(vatPercent) => editState({ vatPercent })}
											disabled={isEditingDisabled}
										/>
									</Grid>
								)}
								<Grid item xs={12}>
									<TextField
										fullWidth
										variant="outlined"
										label={t('product.description', 'Description')}
										placeholder={t(
											'product.descriptionInfo',
											'E.g. product details or usage guidelines',
										)}
										name="description"
										multiline
										rows={5}
										value={state.description.def}
										onChange={(e) =>
											editState({
												description: updateLocaleField(
													state.description,
													e.target.value,
													shopDefaultLanguage,
												),
											})
										}
										margin="normal"
										type="text"
										disabled={isEditingDisabled}
									/>
								</Grid>
								{(!state.external || (state.external && product?.set)) && (
									<Grid item xs={12}>
										<CategorySelect
											value={state.categoryIds}
											onChange={(categoryIds) => editState({ categoryIds })}
											disabled={isEditingDisabled}
											disableCreate={!isAdmin}
										/>
									</Grid>
								)}
							</Grid>
						</ContainerBox>
					</Grid>
					<Grid item xs={12} md={12}>
						<HasFeature
							requiredFeature="SET_PRODUCT"
							fallback={(failedChecks) => {
								return !failedChecks.user ? <AddOnPromoBlock addOn="PACKAGE_PRODUCTS" /> : null;
							}}
						/>
						<HasFeature requiredFeature="SET_PRODUCT">
							<ContainerBox margin="none">
								<Grid container spacing={2}>
									<Grid item xs={12}>
										<Typography variant="h5">
											{t('product.packageProduct', 'Package product')}
										</Typography>
										<Typography variant="caption">
											<Trans
												i18nKey="product.bundleProducts"
												defaults="Bundle multiple products into a <SupportLink>package</SupportLink>"
												components={{
													SupportLink: <ExternalLink href={PACKAGE_PRODUCTS} />,
												}}
											/>
										</Typography>
									</Grid>
									<Grid item xs={12}>
										<CheckboxItem
											label={t('product.isProductPackage', 'This is a package product')}
											checked={state.set}
											onCheckedChange={(checked) => {
												editState({ set: checked, setProductIds: [] });
											}}
											noPadding
											checkboxSize="small"
										/>
									</Grid>
									<Grid item xs={12}>
										{isSetProduct && <Divider style={{ width: '100%' }} />}
									</Grid>
									<Grid item xs={12}>
										{isSetProduct && !isSkidataProduct && <SetProducts />}
									</Grid>
									<Grid item xs={12}>
										{isSkidataPackageProduct && <PackageTicketProducts />}
									</Grid>
								</Grid>
							</ContainerBox>
						</HasFeature>
					</Grid>
				</Grid>
			</Grid>
			<Grid item xs={12} md={4}>
				<Grid container spacing={2}>
					<Hidden mdDown>
						<Grid item xs={12}>
							<ContainerBox margin="none">
								<PublicSelect
									value={!state.additionalProduct}
									onChange={(value) => editState({ additionalProduct: !value })}
									disabled={isEditingDisabled}
								/>
							</ContainerBox>
						</Grid>
					</Hidden>
					<Grid item xs={12}>
						<ContainerBox margin="none">
							<ImageUpload
								value={state.images}
								onChange={(images) => {
									const imgUrls = toMultiImgFormat(images);
									editState({ images: imgUrls, image: !!imgUrls ? imgUrls[0] : undefined });
								}}
								label={t('product.images', 'Images')}
								shopId={shopId}
								disabled={isEditingDisabled}
								isMultiImage={true}
							/>
						</ContainerBox>
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	);
};

export default About;
